import React, { useState } from 'react';
import { normalizeConversionRate, conversionRateReadable } from '../utils/poFxToEur';
import StickyPeekHero from './common/StickyPeekHero';
import {
  Paper, Box, Chip, Stack, Typography, IconButton, Button, ButtonGroup, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText,
  SwipeableDrawer, Collapse,
  useTheme, useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { isAfter, parseISO, differenceInDays } from 'date-fns';
import { visuallyHidden } from '@mui/utils';
import { BOTTOM_SHEET_TRANSITION, BUTTON_RADIUS, FS, TOUCH_TARGET_COARSE, focusRing, hoverOnly } from '../theme/tokens';
import { v2Tone, v2ToneHover } from '../theme/theme';
import { formatMoney, formatDate } from '../utils/format';

export const STATUS_ORDER = ['Draft', 'Order placed', 'Paid', 'In Delivery', 'Done', 'Closed'];

// 🔴 EIN Vokabular fuer Balken, Filter, Pillen und Chips (Konzept §7.1e).
// Bis zum 2026-09-28 fuehrten drei Stellen drei Listen: `STATUS_ORDER` hier,
// `BASE_STATUS_OPTIONS` und `PO_STATUS_PILLS` in `POOverview.jsx`. Folgen:
// „Done“ fehlte im Filter, „partially paid“ war ein Wert, den keine Bestellung
// tragen kann (genau ein Treffer repoweit), und ein `ToDo` aus der Datenbank
// rendete als roher Token neben Nachbarn, die „Order placed“ sagen.
export const PO_STATUSES = STATUS_ORDER;

/** Anzeigename je Schluessel.
 *
 *  `ToDo` ist der VORGABEWERT der Statusspalte (`backend/models.py:725`, ein
 *  blankes `db.String` ohne CHECK) und kommt in keiner Liste vor.
 *  `routes.py:1469` behandelt `("Draft", "ToDo")` gemeinsam — beide treten
 *  also auf. Er wird auf „Draft“ abgebildet, statt als rohes `ToDo` in der
 *  Statusspalte zu stehen. */
export const PO_STATUS_LABEL = {
  Draft: 'Draft',
  ToDo: 'Draft',
  'Order placed': 'Order placed',
  Paid: 'Paid',
  'In Delivery': 'In Delivery',
  Done: 'Done',
  Closed: 'Closed',
  Cancelled: 'Cancelled',
};

/** Kanonischer Schluessel: `ToDo` → `Draft`, alles andere unveraendert.
 *
 *  Ohne das ergaebe `STATUS_ORDER.indexOf('ToDo')` −1: Fortschritt 0 %,
 *  grauer Chip, und `getKeyFacts`/`buildSummary` fielen in ihren `default`
 *  und zeigten gar nichts an. */
export function poStatusKey(status) {
  return status === 'ToDo' ? 'Draft' : status;
}

/** Kurzform fuer die mobilen Filterpillen.
 *
 *  🔴 Sie steht HIER und nicht in `POOverview.jsx`, weil sie sonst die
 *  vierte Schreibweise derselben fuenf Zustaende waere — genau die Lage, die
 *  §7.1e aufloest. Die Pille ist zu schmal fuer „Order placed“ und
 *  „In Delivery“; die Verkuerzung ist eine Anzeigeentscheidung ueber
 *  demselben Schluessel, kein zweites Vokabular. */
export const PO_STATUS_SHORT = {
  'Order placed': 'Ordered',
  'In Delivery': 'Shipping',
};

const TERMINAL = ['Closed', 'Cancelled'];

export const STATUS_COLOR = {
  Draft:          'default',
  'Order placed': 'warning',
  Paid:           'success',
  'In Delivery':  'info',
  Done:           'success',
  Closed:         'default',
  Cancelled:      'error',
};

const DOT_BG_COLOR = {
  default: 'action.disabled',
  warning: 'warning.main',
  success: 'success.main',
  info:    'info.main',
  error:   'error.main',
};

export const CHECKLIST = {
  'Order placed': [
    { id: 'has_items',  label: 'At least one item with qty > 0', anchor: 'has_items',  check: (po) => po.order_items?.some(i => i.quantity > 0) },
    { id: 'has_vendor', label: 'Vendor selected',                anchor: 'has_vendor', check: (po) => !!po.vendor_id },
    { id: 'sent_to_vendor', label: 'Share with vendor', voluntary: true,
      check: (_po, cs) => (cs?.sent_count || 0) > 0 },
  ],
  'Paid': [
    { id: 'sent_to_vendor', label: 'Share with vendor', voluntary: true,
      check: (_po, cs) => (cs?.sent_count || 0) > 0 },
    { id: 'has_invoice', label: 'Invoice uploaded', anchor: 'has_invoice', check: (po) => !!po.invoice_file_url },
    // 🔴 EK-E12 / O6: `total` ist die Wahrheit, `invoice_total` wird daraus
    // abgeleitet. Das Etikett zieht mit — ein Haken, der „Invoice total" sagt
    // und `total` prueft, waere genau die Sorte Geldzeile, deren Etikett und
    // Zahl Verschiedenes meinen.
    // 🔴 A1 (2026-11-01): `total` und `payments_made` sperren NICHT mehr.
    // `TRANSITION_CONFIG["Paid"]` (`backend/purchase_orders/transitions.py:358`)
    // prueft Rechnung und Gutschriften — Gesamtsumme und Zahlungseintrag kennt
    // der Server beim Uebergang nicht. Ein Knopf, der strenger sperrt als der
    // Server, schickt den Bediener auf den Seitenweg ueber das Statusmenue.
    // Die Zeilen bleiben sichtbar: der Nachweis verschwindet nicht, er haelt
    // nur den Knopf nicht mehr an.
    { id: 'has_total',   label: 'Order total confirmed', voluntary: true, anchor: 'has_total',   check: (po) => !!po.total && Number(po.total) > 0 },
    { id: 'has_payment', label: 'Payment entry added',   voluntary: true, anchor: 'has_payment', check: (po) => po.payments_made?.length > 0 },
    // 🔴 `normalizeConversionRate` statt `> 0`: seit dem 2026-09-08 verweigert das
    // Backend einen Kurs ausserhalb EUR/USD ganz (Owner-Entscheid O29), weil die
    // Groesse dort die Richtung nicht traegt. Ein Haken „Conversion rate set" auf
    // einem Kurs, den der Server nicht annimmt, waere eine Zusage, die beim
    // Speichern bricht.
    { id: 'has_fx',      label: 'Conversion rate set',     anchor: 'has_fx',      check: (po) => !po.currency || po.currency === 'EUR' || normalizeConversionRate(po.currency, po.conversion_rate) !== null },
  ],
  'In Delivery': [
    { id: 'has_wh_ref',   label: 'Warehouse reference set', anchor: 'has_wh_ref', check: (po) => !!po.warehouse_reference_valid },
    { id: 'has_tracking', label: 'Tracking confirmed',      anchor: 'has_tracking', check: (po) => !!po.tracking_evidence_valid },
  ],
  // 🔴 U6 (01.11.2026): hier stand `goods_received` mit `check: () => true`.
  //    Ein Pflichtpunkt, der nie scheitern kann, sieht wie eine Pruefung aus und
  //    ist keine — er zaehlte „1/1 required" und war immer gruen. Die Mengen
  //    werden im DIALOG gefragt (`transitionConfig.Done` → `receivedItems`), und
  //    der Server prueft beim Uebergang nur die Gutschriften
  //    (`_check_credits_not_on_other_po`). Eine sperrende Zeile gibt es hier
  //    deshalb nicht — und ohne sperrende Zeile gibt es auch keinen Zaehler.
  'Done': [],
  // 🔴 U2 (01.11.2026): Ankunftsdatum und Inventarbuchung sperren NICHT mehr.
  //    Der Dialog HINTER diesem Knopf fragt sie ab (`TransitionDialog`, Closed),
  //    und der Server prueft sie beim Uebergang — eine Zeile, die den Knopf
  //    sperrt, der den Dialog oeffnet, der die Zeile erfuellt, ist ein Kreis.
  //    Sie bleiben sichtbar: der Nachweis verschwindet nicht, er haelt den Knopf
  //    nur nicht mehr an.
  //    🔴 Die Begruendung ist NICHT „Done setzt nichts" — das aendert die
  //    Done-Automatik. Sie lautet: die Felder gehoeren in den Dialog, der sie
  //    fragt.
  //
  //    Was DAFUER sperrt, ist der Server: `_check_invoice_uploaded` und
  //    `_check_wareneingang_reconciled` lehnen den Uebergang mit 400 ab
  //    (`backend/purchase_orders/transitions.py`). Eine Zeile ohne Gegenstueck
  //    schickt den Bediener in genau dieses 400 — deshalb stehen beide hier.
  'Closed': [
    { id: 'has_invoice', label: 'Invoice uploaded', anchor: 'has_invoice', check: (po) => !!po.invoice_file_url },
    {
      id: 'has_recon',
      label: 'Goods receipt reviewed',
      // `reconState` kommt als dritter Parameter (siehe `checks` unten): der
      // Abgleich liegt NICHT auf der Bestellung, sondern in einem eigenen Abruf.
      // Kein Datensatz = nichts zu pruefen — dasselbe Urteil wie der Server
      // (`rec is None -> True`).
      check: (_po, _cs, recon) => {
        const rec = recon?.reconciliation;
        if (!rec) return true;
        return rec.state !== 'pending' && rec.state !== 'open_delta';
      },
      // Der vorhandene Weg, nicht ein neuer: das Panel „Goods receipt" im
      // Logistics-Abschnitt traegt „Confirm & allow close" und „Dismiss".
      hint: 'Goods receipt needs review — confirm or dismiss it in Logistics.',
      anchor: 'wareneingang-recon',
    },
    // 🔴 R16 W3 (01.11.2026): KEIN Anker mehr. Der Hinweis sagt „asked in the
    //    next step" — der Anker aber sprang auf die ANZEIGE des Datums
    //    (`section-has_arrival`), die nichts abfragt. Ein Knopf, der das
    //    Gegenteil seines eigenen Textes tut, ist schlimmer als Text: die Zeile
    //    sagt jetzt nur noch, wo es weitergeht, und fuehrt nicht selbst hin.
    //    `has_booking` darunter hatte nie einen.
    { id: 'has_arrival', label: 'Actual arrival date set',    voluntary: true, hint: 'asked in the next step', check: (po) => !!po.actual_arrival_date },
    { id: 'has_booking', label: 'Inventory booking confirmed', voluntary: true, hint: 'asked in the next step', check: (po) => !!po.inventory_booked },
  ],
};

const CTA_LABEL = {
  'Order placed': 'Confirm Order',
  'Paid':         'Mark as Paid',
  'In Delivery':  'Mark as In Delivery',
  'Done':         'Mark as Done',
  'Closed':       'Close PO',
};


function getNextStatus(current) {
  const idx = STATUS_ORDER.indexOf(current);
  return idx >= 0 && idx < STATUS_ORDER.length - 1 ? STATUS_ORDER[idx + 1] : null;
}

/** Der EINE Datumsleser dieser Flaeche (Konzept §7.3d).
 *
 *  🔴 Exportiert, weil die Detailseite ihn BRAUCHT statt ihn nachzubauen.
 *  Gemessen am 2026-09-19 standen drei Formate auf einem Bildschirm:
 *  „3. Sept. 2026" in der Statusleiste, „9/3/2026" im Logistics-Panel (vier
 *  locale-lose `toLocaleDateString()`-Aufrufe) und `mm/dd/yyyy` im nativen
 *  Datumsfeld. Zwei davon sind jetzt hier.
 *
 *  ⚠️ Die ANZEIGE des nativen `<input type="date">` laesst sich nicht
 *  erzwingen: Chromium folgt der Browser-/Systemsprache, nicht dem `lang` des
 *  Elements. Die vier Felder bekommen deshalb nur einen `lang`-Hinweis — eine
 *  Bitte, keine Zusage. Ehrlich benannt statt stillschweigend vorausgesetzt. */
export function formatPoDate(d) {
  if (!d) return null;
  // Numerische Tagesform „06.10.2026" (§3c) — der Monatsname „6. Okt. 2026"
  // fiel mit dem Owner-Entscheid vom 06.10. `formatDate` liefert bei
  // unlesbarem Datum „—" statt „Invalid Date".
  return formatDate(d);
}

/**
 * Returns a countdown/overdue label + semantic color for a date string.
 * { text, color }
 */
export function daysLabel(dateStr) {
  if (!dateStr) return null;
  try {
    const d = parseISO(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = differenceInDays(d, today);
    if (diff < 0)  return { text: `${Math.abs(diff)}d overdue`, color: 'error.main' };
    if (diff === 0) return { text: 'Today',   color: 'warning.main' };
    if (diff <= 5)  return { text: `in ${diff}d`, color: 'warning.main' };
    return { text: `in ${diff}d`, color: 'text.disabled' };
  } catch {
    return null;
  }
}

/**
 * Returns an array of { label, value, countdown? } stat cells for the left column,
 * tailored to the current PO status.
 */
function getKeyFacts(po, orderTotal) {
  if (!po) return [];
  const cur = po.currency || 'EUR';
  const fmtAmt = (n) => n && Number(n) > 0 ? formatMoney(n, { currency: cur }) : null;
  // EK-E12 / O6: Rueckfall auf `total`, nicht auf `invoice_total`.
  //
  // 🔴 ALLE VIER Zustaende etikettieren diesen EINEN Wert gleich: „Order total".
  // Bis zum 2026-09-18 hiess er unter `Paid` „Invoice total" und unter `Done`
  // „Total", waehrend `Order placed` und `In Delivery` schon „Order total"
  // sagten — dieselbe Zahl aus derselben Quelle unter drei Namen, und
  // ausgerechnet der prominenteste Zustand trug den Namen der Spalte, die O6
  // zur ABGELEITETEN gemacht hat. Ein Etikett, das „Invoice total" sagt und
  // `total` zeigt, ist genau die Geldzeile, deren Etikett und Zahl
  // Verschiedenes meinen (siehe die Begruendung am Haken `has_total` oben).
  const effectiveTotal = orderTotal ?? po.total;
  const vendor = po.vendor?.metaobject_name || po.vendor?.vendor_name;
  const items = po.order_items || [];
  const itemCount = items.length;
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  const facts = [];

  if (vendor) facts.push({ label: 'Vendor', value: vendor });

  // 🔴 `poStatusKey`, nicht `po.status`: ein `ToDo`-Datensatz fiele sonst in
  // den `default` und die Leiste zeigte GAR KEINE Kennzahl — nicht einmal den
  // Lieferanten, obwohl er oben schon gepusht wurde.
  switch (poStatusKey(po.status)) {
    case 'Draft':
    case 'Order placed': {
      const invTotal = fmtAmt(effectiveTotal);
      if (invTotal) facts.push({ label: 'Order total', value: invTotal });
      if (itemCount > 0) facts.push({ label: 'Items', value: `${itemCount} SKU \u00b7 ${unitWord(totalQty)}` });
      if (po.order_date) facts.push({ label: 'Order date', value: formatPoDate(po.order_date) });
      if (po.payment_date) facts.push({ label: 'Payment due', value: formatPoDate(po.payment_date), countdown: po.payment_date });
      if (po.expected_arrival) facts.push({ label: 'Est. arrival', value: formatPoDate(po.expected_arrival), countdown: po.expected_arrival });
      break;
    }
    case 'Paid': {
      const invTotal = fmtAmt(effectiveTotal);
      if (invTotal) facts.push({ label: 'Order total', value: invTotal });
      const totalPaid = (po.payments_made || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      if (totalPaid > 0) facts.push({ label: 'Paid', value: fmtAmt(totalPaid) });
      if (po.order_date) facts.push({ label: 'Order date', value: formatPoDate(po.order_date) });
      if (po.expected_arrival) facts.push({ label: 'Est. arrival', value: formatPoDate(po.expected_arrival), countdown: po.expected_arrival });
      break;
    }
    case 'In Delivery': {
      const invTotal = fmtAmt(effectiveTotal);
      if (invTotal) facts.push({ label: 'Order total', value: invTotal });
      if (po.delivery_service) facts.push({ label: 'Carrier', value: po.delivery_service });
      if (po.tracking_number) facts.push({ label: 'Tracking', value: po.tracking_number });
      else if (po.no_tracking_provided) facts.push({ label: 'Tracking', value: 'No tracking' });
      if (po.expected_arrival) facts.push({ label: 'Est. arrival', value: formatPoDate(po.expected_arrival), countdown: po.expected_arrival });
      if (po.warehouse_reference) facts.push({ label: 'WH ref.', value: po.warehouse_reference });
      break;
    }
    case 'Done': {
      const invTotal = fmtAmt(effectiveTotal);
      if (invTotal) facts.push({ label: 'Order total', value: invTotal });
      if (itemCount > 0) facts.push({ label: 'Items', value: `${itemCount} SKU \u00b7 ${unitWord(totalQty)}` });
      if (po.expected_arrival) facts.push({ label: 'Est. arrival', value: formatPoDate(po.expected_arrival) });
      if (po.actual_arrival_date) facts.push({ label: 'Arrived', value: formatPoDate(po.actual_arrival_date) });
      break;
    }
    default:
      break;
  }

  return facts;
}

/** „1 unit" / „3 units" — dieselbe Pluralregel wie bei `product`.
 *
 *  🔴 R16 W6 (Nachbesserung 03.11.2026): der Kopfsatz pluralisierte `product`,
 *  aber nicht `units` („1 product (1 units) ordered"), und die Key-Facts
 *  daneben sagten „1 SKU · 1 units". Beides ist an jeder Bestellung mit einer
 *  Einheit sichtbar.
 */
function unitWord(n) {
  return `${n} unit${n === 1 ? '' : 's'}`;
}

function buildSummary(po, commsStatus) {
  if (!po) return null;
  const items = po.order_items || [];
  const itemCount = items.length;
  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const vendor = po.vendor?.metaobject_name || po.vendor?.vendor_name;
  const cur = po.currency || 'EUR';
  const fmtAmt = (n) => n ? formatMoney(n, { currency: cur }) : null;
  const productLine = itemCount > 0
    ? `${itemCount} product${itemCount !== 1 ? 's' : ''} (${unitWord(totalQty)})`
    : null;

  switch (poStatusKey(po.status)) {
    case 'Draft': {
      const sentHint = commsStatus === null
        ? ''
        : commsStatus.sent_count > 0
          ? ` Shared with vendor${commsStatus.last_sent?.to_email ? ` (${commsStatus.last_sent.to_email})` : ''}.`
          : ' Not yet sent to vendor.';
      return productLine
        ? `Draft with ${productLine}${vendor ? ` from ${vendor}` : ''}.${sentHint} Submit when ready to place the order.`
        : `New draft. Add products and a vendor to proceed.${sentHint}`;
    }

    case 'Order placed': {
      const parts = [];
      if (productLine) parts.push(`${productLine}${vendor ? ` from ${vendor}` : ''} ordered${formatPoDate(po.order_date) ? ` on ${formatPoDate(po.order_date)}` : ''}.`);
      if (po.payment_date) parts.push(`Payment due ${formatPoDate(po.payment_date)}${po.payment_terms ? ` (${po.payment_terms})` : ''}.`);
      const sentHint = commsStatus === null
        ? ''
        : commsStatus.sent_count > 0
          ? ` Order shared with vendor.`
          : ` Vendor not yet notified — consider sending the order.`;
      // 🔴 U8 (01.11.2026): der Satz sagte UNBEDINGT „Upload the invoice to
      //    advance" — auch dann, wenn die Rechnung laengst da war und die
      //    Checkliste daneben „Invoice uploaded ✔" zeigte (Befund 11). Ein
      //    Kopfsatz, der eine erledigte Pflicht einfordert, liest sich als
      //    Fehler. Er nennt die Rechnung nur, wenn sie FEHLT.
      // 🔴 R16 B1 / R15 W2 (01.11.2026): der Satz sagte „you can mark this
      //    order as Paid", sobald eine Rechnung da war — und der Knopf daneben
      //    blieb gesperrt, weil `has_fx` sperrt. Genau die Klasse, die U2
      //    repariert hat: ein toter Knopf und daneben ein Versprechen.
      //
      //    Der Satz bindet deshalb an DIESELBE Menge wie der Knopf: `offeneSperren`
      //    ist die Liste der sperrenden Zeilen des NAECHSTEN Status, mit
      //    derselben Pruefung, die `checks` unten aufruft. Kein zweites Urteil,
      //    das auseinanderlaufen kann — die Etiketten kommen aus `CHECKLIST`.
      //
      //    `has_fx` ist die EINE Ausnahme gegenueber dem Server, und sie bleibt
      //    sperrend: der Server prueft beim Wechsel nach `Paid` nur
      //    `conversion_rate > 0` (`transitions.py`, „3-pre) FX rate lock guard"),
      //    die Oberflaeche verlangt einen Kurs, den `normalizeConversionRate`
      //    akzeptiert. Nach `Paid` ist der Kurs gesperrt und steuerrelevant
      //    (O29) — ein falsch dimensionierter Kurs liesse sich nicht mehr
      //    korrigieren. Der Satz nennt deshalb den Kurs, statt den Wechsel zu
      //    versprechen.
      const offeneSperren = (CHECKLIST.Paid || [])
        .filter((c) => !c.voluntary && !c.check(po, commsStatus, null));
      let invoiceHint;
      if (!po.invoice_file_url) {
        // 🔴 Z5 (UX-Runde 2c, 03.11.2026; R16 Schluss W-B der Runde 1): der
        //    Upload raeumt nur `has_invoice`. Fuer eine Waehrung ausserhalb
        //    EUR/USD bleibt `has_fx` dauerhaft offen — „to advance" versprach
        //    damit einen Fortschritt, den die Handlung nicht bringt. Die
        //    Handlung bleibt genannt, die Folge faellt weg.
        //    🔴 R15 W4 (Nachbesserung 03.11.2026): die erste Fassung band das an
        //    die WAeHRUNGSLESBARKEIT — und liess damit USD OHNE Kurs weiter
        //    „to advance" sagen, obwohl `has_fx` dann offen bleibt. Der Satz
        //    haengt jetzt an den Sperren, die NACH dem Upload noch stehen:
        //    dieselbe Menge wie der Knopf, nur ohne `has_invoice` (das raeumt
        //    der Upload). Bleibt keine, ist der Fortschritt zugesagt.
        const sperrenNachUpload = offeneSperren.filter((c) => c.id !== 'has_invoice');
        invoiceHint = sperrenNachUpload.length === 0
          ? ' Upload the invoice to advance.'
          : ' Upload the invoice.';
      } else if (offeneSperren.length === 0) {
        invoiceHint = ' The invoice is uploaded — you can mark this order as Paid.';
      } else if (offeneSperren.some((c) => c.id === 'has_fx')) {
        // 🔴 R16 C1 (Schlussrunde, 2026-11-02): fuer eine Waehrung ausserhalb
        //    EUR/USD kann „set it" nicht gelingen — `normalizeConversionRate`
        //    liest sie nie, der Server nimmt den Kurs nicht an. Dann sagt der
        //    Satz dasselbe wie die Zeilen-Caption, mit derselben Bedingung
        //    (`conversionRateReadable`), statt eine unmoegliche Handlung zu
        //    verlangen.
        invoiceHint = conversionRateReadable(po.currency)
          ? ' The invoice is uploaded, but the conversion rate is still open — set it to mark this order as Paid.'
          : ` The invoice is uploaded, but ${po.currency} rates cannot be checked automatically — place this order in EUR, or ask for ${po.currency} to be enabled.`;
      } else {
        invoiceHint = ' The invoice is uploaded, but this order cannot advance yet.';
      }
      return (parts.join(' ') || 'Order placed.') + sentHint + invoiceHint
        + ' A payment can be recorded later.';
    }

    case 'Paid': {
      const total = fmtAmt(po.total);   // EK-E12 / O6
      // 🔴 Regel-16-Warnung 4 (01.11.2026): seit der Knopf offen ist, ist
      // `Paid` auch ohne Zahlungseintrag erreichbar. „Invoice paid" behauptete
      // dann eine Zahlung, die es nicht gibt — der Chip „Payment open" steht
      // weiter unten in der Finance-Karte, nicht hier. Mit Eintrag bleibt der
      // Satz; ohne Eintrag nennt er den Zustand und behaelt Betrag und Folge.
      const paid = (po.payments_made || []).length > 0;
      const head = paid
        ? `Invoice paid${total ? ` \u2014 ${total}` : ''}`
        : `Marked as paid, no payment recorded yet${total ? ` \u2014 order total ${total}` : ''}`;
      return `${head}. Arrange shipping and add a warehouse reference and tracking number to proceed.`;
    }

    case 'In Delivery': {
      return [
        'Shipment in transit.',
        po.delivery_service && `Carrier: ${po.delivery_service}.`,
        formatPoDate(po.expected_arrival) && `Expected arrival: ${formatPoDate(po.expected_arrival)}.`,
        // U6: die Mengen werden im naechsten Schritt bestaetigt — der Dialog
        // nach „Mark as Done" fragt angenommen/geliefert je Position. Das stand
        // nirgends, solange der Scheinhaken „Goods received" die Liste trug.
        'Confirm goods received at the warehouse to mark as Done — the accepted quantities are entered in that step.',
      ].filter(Boolean).join(' ');
    }

    case 'Done':
      return 'Goods received at the warehouse. Set the actual arrival date and confirm inventory booking to close this order.';

    case 'Closed':
      return `Order closed${formatPoDate(po.actual_arrival_date) ? ` \u2014 arrived ${formatPoDate(po.actual_arrival_date)}` : ''}. All done.`;

    default:
      return null;
  }
}

// One stat cell in the desktop key-facts row
function StatCell({ label, value, countdown, isFirst }) {
  const cd = countdown ? daysLabel(countdown) : null;
  return (
    <Box
      sx={{
        // 🔴 Unter `md` 2x2 statt einer Zeile (2026-10-20). In einer Zeile
        //    blieben je Zelle ~70 px, und `noWrap` kuerzte die Werte zu
        //    „31.07.20…" — ein Datum, das man nicht lesen kann. Mit 45 % Basis
        //    passen zwei je Zeile, und die Werte stehen vollstaendig da.
        flex: { xs: '1 1 45%', lg: '1 1 0' },
        minWidth: 0,
        pl: { xs: 0, lg: isFirst ? 0 : 2 },
        py: { xs: 0.25, md: 0 },
        borderLeft: { xs: 'none', lg: isFirst ? 'none' : '1px solid' },
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="caption"
        display="block"
        sx={{
          fontSize: '0.63rem',
          textTransform: 'uppercase',
          letterSpacing: '0.055em',
          color: 'text.secondary',
          mb: 0.375,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        noWrap
        sx={{ fontSize: '0.82rem', lineHeight: 1.3 }}
      >
        {value}
      </Typography>
      {cd && (
        <Typography
          variant="caption"
          display="block"
          sx={{ fontSize: '0.68rem', color: cd.color, lineHeight: 1.3, mt: 0.125 }}
        >
          {cd.text}
        </Typography>
      )}
    </Box>
  );
}

/**
 * StatusStepper — der beschriftete Statusverlauf (Konzept §7.2e, Posten #13).
 *
 * @description
 *   Loest den sieben Segmenten ohne Legende ab. Auf PO209 (Paid) leuchteten
 *   drei gruen, und ein Leser konnte nicht sagen, ob das vierte „In Delivery“
 *   heisst. Jetzt traegt das AKTUELLE Segment seinen Namen dauerhaft, jedes
 *   andere seinen Namen bei Hover/Fokus.
 *
 *   🔴 `role="meter"`, NICHT `progressbar` — dieselbe Entscheidung wie
 *   `AgingBar` und aus demselben Grund: `frontend/tests/rma/helpers.ts`
 *   wartet beim Laden jeder Seite darauf, dass JEDES `[role="progressbar"]`
 *   `aria-valuenow` traegt. Ein Meter ist eine Anzeige in einem Bereich, keine
 *   Aufgabenvollendung.
 *
 *   🔴 Und der Wert ist ehrlich: „Schritt n von 6“, nicht „n Prozent“. Die
 *   alte Prozentzahl behauptete, `Paid` sei 40 % einer Bestellung — die
 *   Lebenszyklusabschnitte sind aber nicht gleich lang, die Zahl war eine
 *   Fiktion im Gewand einer Messung.
 */
function StatusStepper({ currentIdx, height = 5, compact = false }) {
  const hasCurrent = currentIdx >= 0 && currentIdx < STATUS_ORDER.length;
  const currentLabel = hasCurrent ? (PO_STATUS_LABEL[STATUS_ORDER[currentIdx]] || STATUS_ORDER[currentIdx]) : null;
  return (
    <Box
      role="meter"
      aria-label="Purchase order progress"
      aria-valuemin={1}
      aria-valuemax={STATUS_ORDER.length}
      aria-valuenow={hasCurrent ? currentIdx + 1 : 1}
      aria-valuetext={currentLabel ? `Step ${currentIdx + 1} of ${STATUS_ORDER.length}: ${currentLabel}` : 'Unknown status'}
      sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0, alignItems: 'center' }}
    >
      {STATUS_ORDER.map((s, i) => {
        const isCurrent = i === currentIdx;
        const label = PO_STATUS_LABEL[s] || s;
        return (
          <Tooltip key={s} title={label} arrow disableInteractive>
            <Box sx={{ flex: isCurrent && !compact ? '0 1 auto' : 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: compact ? 0 : 10,
                  height,
                  borderRadius: 99,
                  bgcolor: i < currentIdx
                    ? 'success.main'
                    : isCurrent
                      ? (DOT_BG_COLOR[STATUS_COLOR[s]] || 'primary.main')
                      : 'action.disabled',
                  transition: 'background-color 0.3s ease',
                }}
              />
              {isCurrent && !compact && (
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {label}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

/**
 * POStatusBar
 * Props:
 *   po           — full PO object
 *   orderTotal   — number | null — live-calculated total from PODetail (same as Finance section);
 *                  falls back to po.total when null (EK-E12 / O6 — `total` is
 *                  the truth, `invoice_total` is derived from it)
 *   onTransition — fn() — triggers next-status transition
 *   onCancel     — fn() — triggers Cancelled transition
 *   onScrollTo   — fn(sectionId) — scrolls to a section anchor
 *   onAction     — fn(actionType: string) — dispatches a quick action to parent
 *   commsStatus  — { sent_count, last_sent, order_changed_since_send } | null
 *   reconState   — { reconciliation, latest_event, history } | null — der Wareneingang-
 *                  Abgleich der Detailseite; `has_recon` (Closed) liest daraus.
 *   reconLage    — 'laedt' | 'da' | 'fehler' — der ABRUFZUSTAND dazu. Ohne ihn
 *                  hiess `null` drei Dinge gleichzeitig („laedt", „gescheitert",
 *                  „kein Datensatz"), und die Zeile zeigte in allen dreien den
 *                  gruenen Haken. Vorgabe `laedt`: ein Aufrufer, der den Zustand
 *                  nicht kennt, sperrt NICHT (Regel 20 — weg vom Hindernis).
 */
export default function POStatusBar({ po, orderTotal, onTransition, onCancel, onScrollTo, onAction, commsStatus, emailIncludesPrices, reconState = null, reconLage = 'laedt' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  // Prices are decided by the model from the staff note (default exclude). This
  // is a read-only indicator of the latest vendor-email draft, not a control.
  // Sticky/compact peek lives in the shared StickyPeekHero primitive — no
  // scroll listener or collapsed state here anymore.

  if (!po) return null;

  // 🔴 Endzustaende bekommen einen KOPF, kein Loch (Konzept §7.2d / Posten #16).
  // Bis zum 2026-09-28 stand hier `return null`. Der Aufrufer rendert die
  // Leiste unbedingt (`PODetail.jsx`), also klaffte auf Closed/Cancelled eine
  // Luecke — und weil „Download PDF“ und „Copy text“ NUR in diesem Kebab
  // lebten, konnte eine geschlossene Bestellung sich nicht mehr ausfuehren.
  // Ein Ende, kein Fehler.
  const isTerminal = TERMINAL.includes(poStatusKey(po.status));

  const currentStatus = poStatusKey(po.status) || 'Draft';
  const nextStatus = getNextStatus(currentStatus);
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);

  const checkDefs = nextStatus ? (CHECKLIST[nextStatus] || []) : [];
  // U2: `reconState` als dritter Parameter — der Wareneingang-Abgleich liegt
  // nicht auf der Bestellung, sondern in einem eigenen Abruf der Detailseite.
  // 🔴 R16 W4 / R15 C1 (01.11.2026): solange der Abgleich NICHT gemessen ist
  //    (`laedt` oder `fehler`), zeichnet `has_recon` keinen Haken und sperrt
  //    nicht. Die Zeile sagt, dass nicht geprueft wurde — beim Schliessen
  //    urteilt der Server (`_check_wareneingang_reconciled`). „Unbekannt ist
  //    nie bestanden" an der einen Stelle, die eine Freigabe erteilt.
  const checks = checkDefs.map((d) => {
    const passed = d.check(po, commsStatus, reconState);
    if (d.id !== 'has_recon' || reconLage === 'da') return { ...d, passed };
    // 🔴 R15 B1 (Schlussrunde, 2026-11-02): in `laedt` gibt es kein Ziel —
    //    das Panel existiert erst mit einem Datensatz. Ein Anker hier waere ein
    //    Knopf, der nichts tut (genau die Klasse, gegen die U4 angetreten ist).
    //    In `fehler` steht das Fehler-Panel an derselben Stelle, also DARF die
    //    Zeile dorthin springen; der Satz nennt dann den gescheiterten Abruf
    //    statt „nicht geprueft" (R16 W5).
    const zielDa = reconLage === 'fehler';
    return {
      ...d,
      passed: false,
      voluntary: true,
      anchor: zielDa ? d.anchor : undefined,
      hint: zielDa
        ? 'Could not load the goods receipt check — reload it in Logistics.'
        : 'Not checked yet — the server decides this when you close the order.',
    };
  });
  // 🔴 A1 (Grill B2): der Zaehler nennt nur, was SPERRT. Vorher zaehlte er
  // freiwillige Zeilen mit und faerbte „3/5" gruen, waehrend darunter zwei
  // offene Kreise standen. `blockingChecks` ist die Menge, an der der Knopf
  // haengt — dieselbe, die `allPassed` bildet.
  const blockingChecks = checks.filter(c => !c.voluntary);
  const blockingPassed = blockingChecks.filter(c => c.passed).length;
  const allPassed = blockingPassed === blockingChecks.length;

  const keyFacts = getKeyFacts(po, orderTotal);
  const summary = buildSummary(po, commsStatus);

  // Read-only reflection of the model's price decision on the last vendor-email
  // draft. null (no draft yet) → hidden, the default. Prices are opt-in via the
  // note ("mit Preisen"), not a toggle here.
  const pricesIndicator = (
    <Tooltip title="Set by your note — add “mit Preisen” to include prices in the vendor email">
      <Chip
        size="small"
        variant="outlined"
        label={emailIncludesPrices ? 'Prices: included' : 'Prices: hidden'}
        sx={{ fontSize: '0.7rem', height: 22 }}
      />
    </Tooltip>
  );

  // Context alerts
  const contextAlerts = [];
  if (currentStatus === 'Order placed' && po.payment_date) {
    const due = parseISO(po.payment_date);
    if (isAfter(new Date(), due)) {
      const days = differenceInDays(new Date(), due);
      contextAlerts.push(
        // K-3: umrandet = Schrift `palette.warning.main` auf Papier (3,113:1
        // hell, unter 4,5:1); der v2-Ton traegt in beiden Modi.
        <Chip key="overdue" label={`Payment ${days}d overdue`} size="small" variant="outlined" sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, fontSize: '0.7rem', height: 22 })} />
      );
    }
  }
  if (currentStatus === 'In Delivery' && po.expected_arrival) {
    const arr = parseISO(po.expected_arrival);
    if (isAfter(new Date(), arr)) {
      const days = differenceInDays(new Date(), arr);
      contextAlerts.push(
        <Chip key="late" label={`${days}d late`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
      );
    }
  }

  const handleCancel = () => {
    setMenuAnchor(null);
    setSheetOpen(false);
    onCancel?.();
  };

  // 🔴 Die Dokumentenausgaben haengen NICHT mehr an `hasSentToVendor`.
  // Das war eine zweite, ungenannte Sackgasse: `sent_to_vendor` steht nur in
  // den Checklisten von `Order placed` und `Paid`. Auf `In Delivery` und `Done`
  // fand `checks.find(c => c.id === 'sent_to_vendor')` nichts — also gab es
  // dort WEDER das Kebab noch das Share-Panel, und eine unterwegs befindliche
  // Bestellung konnte ihr eigenes PDF nicht mehr herunterladen. Das Konzept
  // nannte nur Closed/Cancelled (Posten #16); gemessen sind es vier Zustaende.
  const menuItems = (
    <>
      <MenuItem key="pdf" onClick={() => { onAction?.('download_pdf'); setSheetOpen(false); setMenuAnchor(null); }}>
        <ListItemIcon><PictureAsPdfOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Download PDF</ListItemText>
      </MenuItem>
      <MenuItem key="copy" onClick={() => { onAction?.('copy_text'); setSheetOpen(false); setMenuAnchor(null); }}>
        <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Copy text</ListItemText>
      </MenuItem>
      {!isTerminal && (
        <MenuItem sx={{ color: 'error.main' }} onClick={handleCancel}>
          Cancel PO
        </MenuItem>
      )}
    </>
  );

  // Desktop/mobile checklist item row — label + status icon only, no action buttons.
  //
  // 🔴 U4 (01.11.2026): eine OFFENE Zeile mit Anker ist bedienbar und springt zu
  //    ihrem Abschnitt. `onScrollTo` stand bis dahin nur im JSDoc dieser
  //    Komponente — keine Zeile rief es, und auf dem Telefon lag das Ziel
  //    dreitausend Pixel weiter unten. Eine Zeile OHNE Anker bleibt Text: ein
  //    Knopf, der nichts tut, ist schlimmer als keiner. Eine erledigte Zeile ist
  //    kein Ziel.
  const renderCheckItem = (c) => {
    const isSent = c.id === 'sent_to_vendor';
    const klickbar = Boolean(c.anchor) && !c.passed && typeof onScrollTo === 'function';
    const springe = () => onScrollTo(c.anchor);
    const stale = isSent && c.passed && commsStatus?.order_changed_since_send;
    const sentAt = isSent && commsStatus?.last_sent?.sent_at
      ? formatDate(commsStatus.last_sent.sent_at)
      : null;
    return (
      <Box
        key={c.id}
        role={klickbar ? 'button' : undefined}
        tabIndex={klickbar ? 0 : undefined}
        onClick={klickbar ? springe : undefined}
        onKeyDown={klickbar ? (e) => {
          // Enter UND Leertaste — ein div mit role="button" bringt beides nicht
          // mit. `preventDefault` auf Space, sonst scrollt die Seite mit.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            springe();
          }
        } : undefined}
        sx={klickbar ? {
          cursor: 'pointer',
          // Regel 22 §8: 44 px Trefferflaeche auf grobem Zeiger (Telefon).
          // Ueber den Token, nicht als rohe Zahl — die Zeile ist ~20 px hoch.
          ...TOUCH_TARGET_COARSE,
          // Hausform: `BUTTON_RADIUS` ist ein px-Wert und wird in `sx` ausgeschrieben
          // (Regel 22 §4) — die nackte Zahl waere ein VIELFACHES von 6.
          borderRadius: `${BUTTON_RADIUS}px`,
          // Fokusring ueber den Haushelfer, nicht als Literal (Regel 22).
          '&:focus-visible': focusRing(),
          // 🔴 R16 B2 (01.11.2026): die Zeile sah wie eine tote aus — der
          //    einzige Unterschied war `cursor: pointer`, auf einem groben
          //    Zeiger unsichtbar. Hover aus dem Theme-Ton, kein Literal.
          //    🔴 K-H (04.11.2026): der Hintergrund gehoert hinter die
          //    Zeigerabfrage — sonst bleibt er auf einem Touchgeraet nach dem
          //    Antippen stehen. Form: `hoverOnly` aus `theme/tokens.js`.
          ...hoverOnly({ bgcolor: 'action.hover' }),
        } : undefined}
      >
        <Stack direction="row" alignItems="flex-start" gap={0.75}>
          {c.passed
            ? <CheckCircleIcon color={stale ? 'warning' : 'success'} sx={{ fontSize: 17, flexShrink: 0, mt: '2px' }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: 17, flexShrink: 0, mt: '2px', color: 'text.disabled' }} />
          }
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="baseline" columnGap={0.75} flexWrap="wrap">
              <Typography
                variant="body2"
                sx={{
                  color: c.passed && !stale ? 'text.disabled' : 'text.primary',
                  textDecoration: c.passed && !stale ? 'line-through' : 'none',
                  lineHeight: 1.45,
                }}
              >
                {c.label}
              </Typography>
              {/* 🔴 Regel-16-Warnung 3 (01.11.2026): das Haekchen ist ein
                  `SvgIcon` mit `aria-hidden`. Ohne diesen Text hoert ein
                  Screenreader bei „Invoice uploaded" dasselbe wie bei einer
                  offenen Zeile — der Zustand haenge dann allein am Icon.
                  Form wie im Repo ueblich: `visuallyHidden` aus `@mui/utils`. */}
              <Box component="span" sx={visuallyHidden}>{c.passed ? 'done' : 'open'}</Box>
              {/* 🔴 A1 (Grill B2): eine OFFENE freiwillige Zeile sah aus wie eine
                  sperrende — es gab kein Merkmal ausser „der Knopf geht trotzdem".
                  Das Merkmal steht jetzt an der Zeile.
                  🔴 Regel-16-Blocker B1 (01.11.2026): das Wort heisst „does not
                  block", nicht „optional". „optional" ist ein Wahl-Wort und las
                  sich als „darfst du ueberspringen" — genau die Formulierung, die
                  zum Ueberspringen verleitet. Was hier steht, ist eine Tatsache
                  ueber den Knopf, keine Wahl.
                  🔴 `sent_to_vendor` traegt den Marker NICHT (Warnung 2): sein
                  eigener Kasten darunter ist die Handlung und unterscheidet die
                  Zeile bereits; ein Marker dort widerspraeche der primaeren
                  Aktion daneben. */}
              {c.voluntary && !c.passed && c.id !== 'sent_to_vendor' && !c.hint && (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
                  does not block
                </Typography>
              )}
            </Stack>
            {isSent && c.passed && commsStatus?.last_sent?.to_email && (
              <Typography variant="caption" sx={{ color: stale ? 'warning.main' : 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                {stale ? '⚠ Changed since last send · ' : ''}{commsStatus.last_sent.to_email}{sentAt ? ` · ${sentAt}` : ''}
              </Typography>
            )}
            {/* 🔴 `has_fx` ist NICHT `voluntary`, sperrt also den Weiter-Knopf.
                Bei einer Waehrung ausserhalb EUR/USD nimmt der Server ueberhaupt
                keinen Kurs an (Owner-Entscheid O29) — der Haken bliebe dann fuer
                immer offen, und die einzige Auskunft waere „N items remaining".
                Ein dauerhaft unerfuellbarer Punkt ohne Grund sieht aus wie ein
                Fehler; deshalb steht der Grund hier. */}
            {/* 🔴 Regel-16-Blocker B1 (01.11.2026): die Folge des Weiterklickens
                stand nirgends auf dem Bildschirm — kein Dialog fuer `Paid`, kein
                Banner (Entscheid 6), der Chip „Payment open" liegt weiter unten
                in der Finance-Karte. Ein Marker allein sagt „sperrt nicht", nicht
                was dann passiert. Muster wie der `has_fx`-Grund darunter: eine
                Caption an der Zeile, ein Satz. */}
            {/* U2: eine Zeile, die ihren Grund selbst kennt, sagt ihn — statt des
                allgemeinen „does not block" („asked in the next step") oder als
                Wegweiser zum vorhandenen Abgleich-Panel. */}
            {c.hint && !c.passed && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                {c.hint}
              </Typography>
            )}
            {c.id === 'has_payment' && !c.passed && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                Recording it later is fine. The payment reminder stops once this order is marked Paid.
              </Typography>
            )}
            {/* 🔴 R16 C1 (Schlussrunde, 2026-11-02): die Bedingung hing am
                eingetragenen Kurs (`conversion_rate > 0`). Fuer eine Waehrung
                ausserhalb EUR/USD ist der Grund aber derselbe, ob ein Wert im
                Feld steht oder nicht — und ohne diese Caption sagte der
                Kopfsatz „set it" ohne Widerspruch daneben. Beide lesen jetzt
                `conversionRateReadable`. */}
            {c.id === 'has_fx' && !c.passed && !conversionRateReadable(po.currency) && (
              <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', lineHeight: 1.3 }}>
                {po.currency} rates cannot be checked automatically — place this order in EUR,
                or ask for {po.currency} to be enabled.
              </Typography>
            )}
            {/* 🔴 Regel-16-Blocker B1 (01.11.2026). Seit die Checkliste dem Urteil des
                Servers folgt, steht ein Wert im Feld und die Zeile ist trotzdem offen.
                Ohne diesen Satz sieht das aus wie ein Fehler: der Nutzer erfährt nicht,
                dass sein Wert das falsche FORMAT hat — und der Dialog, der es sagen
                koennte, ist bei ungueltigem Wert gar nicht erreichbar. */}
            {c.id === 'has_wh_ref' && !c.passed && po.warehouse_reference && (
              <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', lineHeight: 1.3 }}>
                "{po.warehouse_reference}" is not an IN-number — it must look like IN-000026526.
                Correct it in the edit form.
              </Typography>
            )}
          </Box>
          {/* 🔴 R16 B2 (01.11.2026): das nachlaufende Chevron ist das sichtbare
              Signal der Handlung — dekorativ (`aria-hidden`), der zugaengliche
              Name der Zeile bleibt ihr Inhalt. Nur an Zeilen, die wirklich
              springen; eine tote Zeile traegt keins. */}
          {klickbar && (
            <ChevronRightIcon
              aria-hidden="true"
              data-testid="zeilen-chevron"
              // Regel 22: Groesse aus dem Token, nicht als rohe Zahl.
              // 🔴 R16 W3 (Schlussrunde, 2026-11-02): `text.disabled` sind auf
              //    Weiss ~2,7:1 — unter WCAG 1.4.11 (3:1) fuer bedienungs-
              //    relevante Grafik, und es war das EINZIGE sichtbare Signal
              //    der Zeile. `text.secondary` liegt darueber.
              sx={{ fontSize: FS.xl, flexShrink: 0, color: 'text.secondary', mt: '1px' }}
            />
          )}
        </Stack>
      </Box>
    );
  };

  // ── Endzustand: ein Kopf, kein Loch (Konzept §7.2d / Posten #16) ────────────
  // `buildSummary` traegt fuer `Closed` schon das Ankunftsdatum ("
  // — arrived <Datum>"). `Cancelled` hat keinen Zweig, deshalb der Rueckfall.
  const terminalBar = (
    <Paper variant="outlined" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2.5 } }}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Chip
          label={PO_STATUS_LABEL[currentStatus] || currentStatus}
          size="small"
          color={STATUS_COLOR[currentStatus] || 'default'}
          sx={{ fontWeight: 600, flexShrink: 0 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
          {summary || 'This purchase order is cancelled.'}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label="More actions"
          sx={{ flexShrink: 0 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuItems}
      </Menu>
    </Paper>
  );

  if (isTerminal) {
    return terminalBar;
  }

  // Compact bar rendered in the StickyPeekHero overlay when the hero scrolls
  // off. Desktop only — on mobile the expanded hero is already compact
  // enough that a second overlay would be noise.
  const compactBar = !isMobile && (
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Chip label={currentStatus} size="small" color={STATUS_COLOR[currentStatus] || 'default'} sx={{ fontWeight: 600, flexShrink: 0 }} />
      <StatusStepper currentIdx={currentIdx} height={3} compact />
      {nextStatus && (
        allPassed ? (
          <Button size="small" variant="contained" onClick={onTransition} sx={{ flexShrink: 0, px: 2 }}>
            {CTA_LABEL[nextStatus] || `${nextStatus} \u2192`}
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {/* U6: „complete" las sich als „fertig"; gezaehlt wird aber, was
                noch FEHLT. Dieselbe Vokabel wie die zwei anderen Zaehler. */}
            {blockingPassed}/{blockingChecks.length} required
          </Typography>
        )
      )}
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions" sx={{ flexShrink: 0 }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const expandedBar = (
    <Paper
      elevation={0}
      sx={{
        py: { xs: 1.5, sm: 2.25 },
        px: { xs: 1.5, sm: 2.5 },
        background: (t) =>
          t.palette.mode === 'light' ? 'rgba(248,250,252,0.92)' : 'rgba(15,23,42,0.92)',
        backdropFilter: 'blur(14px) saturate(160%)',
        WebkitBackdropFilter: 'blur(14px) saturate(160%)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: (t) =>
          t.palette.mode === 'light'
            ? '0 2px 16px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.85)'
            : '0 2px 16px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >

      {/* ══════════════════ MOBILE LAYOUT ══════════════════ */}
      {isMobile && (
        <>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              label={currentStatus}
              size="small"
              color={STATUS_COLOR[currentStatus] || 'default'}
              sx={{ fontWeight: 600, flexShrink: 0 }}
            />
            <StatusStepper currentIdx={currentIdx} height={5} compact />
            <IconButton size="small" onClick={() => setSheetOpen(true)} aria-label="More actions" sx={{ flexShrink: 0 }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Collapse in={!!nextStatus} timeout={280}>
            <Box sx={{ pt: 1.75, mt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              {summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: contextAlerts.length > 0 ? 0.75 : 1.75, lineHeight: 1.5 }}>
                  {summary}
                </Typography>
              )}
              {contextAlerts.length > 0 && (
                <Stack direction="row" gap={1} sx={{ mb: 1.75 }}>
                  {contextAlerts}
                </Stack>
              )}
              {checkDefs.length > 0 && (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}>
                      To reach {nextStatus}
                    </Typography>
                    {/* U6: kein Zaehler ohne sperrende Zeile. „0 / 0 required"
                        behauptet, es fehle etwas, wo nichts zu erfuellen ist. */}
                    {blockingChecks.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {blockingPassed} / {blockingChecks.length} required
                      </Typography>
                    )}
                  </Stack>
                  <Stack spacing={0.75} mb={1.75}>
                    {checks.map(renderCheckItem)}
                  </Stack>
                </>
              )}

              {/* ── Mobile vendor comm panel ── */}
              {checks.some(c => c.id === 'sent_to_vendor') && (() => {
                const sentCheck = checks.find(c => c.id === 'sent_to_vendor');
                const stale = commsStatus?.order_changed_since_send;
                const sentAt = commsStatus?.last_sent?.sent_at
                  ? formatDate(commsStatus.last_sent.sent_at)
                  : null;

                if (!sentCheck.passed) {
                  return (
                    <Box sx={{
                      mb: 1.75, p: 1.5, borderRadius: 2,
                      border: '1px solid', borderColor: 'primary.light',
                      bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(44,56,126,0.04)' : 'rgba(147,197,253,0.06)',
                    }}>
                      <Stack direction="row" alignItems="center" gap={0.75} mb={1}>
                        <EmailOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ flex: 1 }}>
                          Share with vendor
                        </Typography>
                        {pricesIndicator}
                      </Stack>
                      <Button variant="contained" fullWidth size="small" startIcon={<EmailOutlinedIcon />}
                        onClick={() => onAction?.('send_email')}>
                        Send by email
                      </Button>
                    </Box>
                  );
                }
                if (stale) {
                  return (
                    <Box sx={{
                      mb: 1.75, p: 1.5, borderRadius: 2,
                      border: '1px solid', borderColor: 'warning.light',
                      bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(245,158,11,0.05)' : 'rgba(251,191,36,0.06)',
                    }}>
                      <Stack direction="row" alignItems="center" gap={0.75} mb={1}>
                        <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ flex: 1 }}>
                          Changed since last send
                        </Typography>
                        {pricesIndicator}
                      </Stack>
                      <Button variant="outlined" fullWidth size="small" startIcon={<EmailOutlinedIcon />}
                        sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, ...v2ToneHover(t, 'warn') })}
                        onClick={() => onAction?.('send_email')}>
                        Resend updated order
                      </Button>
                    </Box>
                  );
                }
                // Sent and clean
                return (
                  <Box sx={{
                    mb: 1.75, p: 1.25, borderRadius: 2,
                    border: '1px solid', borderColor: 'success.light',
                    bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(22,163,74,0.04)' : 'rgba(22,163,74,0.06)',
                  }}>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} color="success.main">Sent to vendor</Typography>
                        {commsStatus?.last_sent?.to_email && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {commsStatus.last_sent.to_email}{sentAt ? ` · ${sentAt}` : ''}
                          </Typography>
                        )}
                      </Box>
                      <Button size="small" variant="text" sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '0.7rem' }}
                        onClick={() => onAction?.('send_email')}>
                        Resend
                      </Button>
                    </Stack>
                  </Box>
                );
              })()}

              <Button variant="contained" fullWidth disabled={!allPassed} onClick={onTransition} sx={{ py: 1 }}>
                {CTA_LABEL[nextStatus] || `${nextStatus} \u2192`}
              </Button>
            </Box>
          </Collapse>
        </>
      )}

      {/* ══════════════════ DESKTOP LAYOUT ══════════════════ */}
      {!isMobile && (
        <>
            {/* Expanded desktop (compact variant lives in the StickyPeekHero overlay) */}
            <>
              {/* Beschrifteter Statusverlauf statt sieben namenloser Segmente */}
              <Stack direction="row" alignItems="center" gap={1.5}>
                <StatusStepper currentIdx={currentIdx} height={5} />
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions" sx={{ flexShrink: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Stack>

              {/* Two-column body */}
              {nextStatus && (() => {
                const sentCheck = checks.find(c => c.id === 'sent_to_vendor');
                const otherChecks = checks;  // keep sent_to_vendor in checklist as reminder
                // A1 (Grill B2): gezaehlt wird nur, was sperrt.
                const otherBlocking = otherChecks.filter(c => !c.voluntary);
                const otherPassed = otherBlocking.filter(c => c.passed).length;
                const stale = commsStatus?.order_changed_since_send;
                const sentAt = commsStatus?.last_sent?.sent_at
                  ? formatDate(commsStatus.last_sent.sent_at)
                  : null;

                // 🔴 **Die 300 px waren fest** (2026-10-20). In einem **270 px**
                // breiten Elternteil (Paper 312 minus 20/20 padding und 1/1 border,
                // PO-Detail bei 640 px Viewport) erzwang die zweite Spalte 300 px
                // und schob die Seite ueber die Viewportbreite.
                // 🔴 **Die Zahl haengt an der Bestellung:** bei 640 gemessen PO305
                // 828, PO198 943, PO197 962 — die Statusleiste traegt statusabhaengig
                // unterschiedlich viel Inhalt. 828 ist also ein Fall, nicht der Regelfall.
                // Unter `md` stapeln; die Trennlinie wandert von rechts nach unten.
                return (
                  <Box sx={{ display: 'grid', // 🔴 `minmax(0, 1fr)` statt `1fr`: ein nacktes `1fr` ist `minmax(auto, 1fr)` und
                  //    schrumpft NICHT unter die min-content-Breite seiner Spur. Die Kennzahlen-
                  //    Zeile (vier `noWrap`-Zellen) hat ~313 px min-content und schob die Karte
                  //    bei 900 um 43 px auf (gemessen PO198: 943 gegen 900).
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 300px' }, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>

                    {/* ── Left: context + vendor comm panel ── */}
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', borderColor: 'divider',
                      // 🔴 Ohne `minWidth: 0` waechst ein Flex-Kind nicht unter seine
                      //    min-content-Breite — dieselbe Ursache eine Ebene tiefer.
                      minWidth: 0,
                      pr: { xs: 0, md: 3 },
                      pb: { xs: 2, md: 0 },
                      borderRight: { xs: 'none', md: '1px solid' },
                      borderBottom: { xs: '1px solid', md: 'none' },
                    }}>

                      {/* Status chip + alerts */}
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                        <Chip label={currentStatus} size="small" color={STATUS_COLOR[currentStatus] || 'default'} sx={{ fontWeight: 600 }} />
                        {contextAlerts}
                      </Stack>

                      {/* Summary */}
                      {summary && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary', mb: sentCheck ? 2 : 'auto', pb: !sentCheck && keyFacts.length > 0 ? 2 : 0 }}>
                          {summary}
                        </Typography>
                      )}

                      {/* ── Vendor communication action panel ── */}
                      {sentCheck && (() => {
                        if (!sentCheck.passed) {
                          // Not yet sent
                          return (
                            <Box sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'primary.light',
                              bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(44,56,126,0.04)' : 'rgba(147,197,253,0.06)',
                            }}>
                              <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
                                <EmailOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                  Share this order with your vendor
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                                Send the order details to your vendor so they can prepare the shipment.
                              </Typography>
                              <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                                {/* 🔴 R16 H1: px-Zeichenkette wie die zwei Kinder darunter. Eine
                                    nackte Zahl waere in MUI ein Vielfaches der Formeinheit
                                    (`BUTTON_RADIUS` -> 36 px statt 6) — dieselbe Falle wie
                                    `KnVerknuepfungZeile.jsx:146`. */}
                                <ButtonGroup variant="contained" size="small" sx={{ borderRadius: { xs: '99px', sm: `${BUTTON_RADIUS}px` }, overflow: 'hidden' }}>
                                  <Button startIcon={<EmailOutlinedIcon />}
                                    onClick={() => onAction?.('send_email')}
                                    sx={{ borderRadius: { xs: '99px 0 0 99px', sm: `${BUTTON_RADIUS}px 0 0 ${BUTTON_RADIUS}px` }, px: 1.5 }}>
                                    Send by email
                                  </Button>
                                  <Button sx={{ px: 0.5, minWidth: 32, borderRadius: { xs: '0 99px 99px 0', sm: `0 ${BUTTON_RADIUS}px ${BUTTON_RADIUS}px 0` } }}
                                    onClick={e => setShareMenuAnchor(e.currentTarget)}>
                                    <ArrowDropDownIcon fontSize="small" />
                                  </Button>
                                </ButtonGroup>
                                <Menu anchorEl={shareMenuAnchor} open={Boolean(shareMenuAnchor)}
                                  onClose={() => setShareMenuAnchor(null)}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                                  <MenuItem dense onClick={() => { onAction?.('download_pdf'); setShareMenuAnchor(null); }}>
                                    <ListItemIcon><PictureAsPdfOutlinedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>Download PDF</ListItemText>
                                  </MenuItem>
                                  <MenuItem dense onClick={() => { onAction?.('copy_text'); setShareMenuAnchor(null); }}>
                                    <ListItemIcon><ContentCopyOutlinedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>Copy text</ListItemText>
                                  </MenuItem>
                                </Menu>
                                {pricesIndicator}
                              </Stack>
                            </Box>
                          );
                        }
                        if (stale) {
                          // Sent but order changed
                          return (
                            <Box sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'warning.light',
                              bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(245,158,11,0.05)' : 'rgba(251,191,36,0.06)',
                            }}>
                              <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
                                <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
                                <Typography variant="body2" fontWeight={600} color="warning.main">
                                  Order changed since last send
                                </Typography>
                              </Stack>
                              {commsStatus?.last_sent?.to_email && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                                  Last sent to {commsStatus.last_sent.to_email}{sentAt ? ` on ${sentAt}` : ''}. Items have been modified — resend to keep your vendor up to date.
                                </Typography>
                              )}
                              <Button size="small" variant="outlined" startIcon={<EmailOutlinedIcon />}
                                sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, ...v2ToneHover(t, 'warn') })}
                                onClick={() => onAction?.('send_email')}>
                                Resend updated order
                              </Button>
                            </Box>
                          );
                        }
                        // Sent and clean
                        return (
                          <Box sx={{
                            mb: 2,
                            p: 1.75,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'success.light',
                            bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(22,163,74,0.04)' : 'rgba(22,163,74,0.06)',
                          }}>
                            <Stack direction="row" alignItems="center" gap={1}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  Sent to vendor
                                </Typography>
                                {commsStatus?.last_sent?.to_email && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                                    {commsStatus.last_sent.to_email}{sentAt ? ` · ${sentAt}` : ''}
                                  </Typography>
                                )}
                              </Box>
                              <Button size="small" variant="text" sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '0.7rem' }}
                                onClick={() => onAction?.('send_email')}>
                                Resend
                              </Button>
                            </Stack>
                          </Box>
                        );
                      })()}

                      {/* Key facts stat row — always at the bottom of the left column */}
                      {keyFacts.length > 0 && (
                        <Box sx={{ pt: 1.75, borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
                          <Stack direction="row" gap={0}
                            sx={{ overflow: 'hidden', flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
                            {keyFacts.slice(0, 4).map((f, i) => (
                              <StatCell key={i} label={f.label} value={f.value} countdown={f.countdown} isFirst={i === 0} />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>

                    {/* ── Right: checklist + CTA ── */}
                    {/* 🔴 `pl` faellt beim Stapeln weg (Regel-15-Befund, 2026-10-20):
                        die 24 px Einzug gehoerten zur TRENNLINIE rechts. Ohne die
                        Linie rueckte die Checkliste auf dem Telefon nur sinnlos ein. */}
                    <Box sx={{ pl: { xs: 0, md: 3 }, display: 'flex', flexDirection: 'column' }}>
                      {otherChecks.length > 0 && (
                        <>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                              To reach {nextStatus}
                            </Typography>
                            {/* U6: kein Zaehler ohne sperrende Zeile. */}
                            {otherBlocking.length > 0 && (
                              <Typography
                                variant="body2"
                                color={allPassed ? 'success.main' : 'text.secondary'}
                                fontWeight={allPassed ? 700 : 400}
                              >
                                {otherPassed}/{otherBlocking.length} required
                              </Typography>
                            )}
                          </Stack>
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            {otherChecks.map(renderCheckItem)}
                          </Stack>
                        </>
                      )}

                      <Box sx={{ mt: 'auto' }}>
                        <Button variant="contained" fullWidth disabled={!allPassed} onClick={onTransition} sx={{ py: 1 }}>
                          {CTA_LABEL[nextStatus] || `${nextStatus} \u2192`}
                        </Button>
                        {!allPassed && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
                            {otherBlocking.length - otherPassed} item{otherBlocking.length - otherPassed !== 1 ? 's' : ''} remaining
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </>
        </>
      )}

      {/* Desktop overflow menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {menuItems}
      </Menu>

      {/* Mobile bottom sheet */}
      <SwipeableDrawer anchor="bottom" open={sheetOpen} onOpen={() => setSheetOpen(true)}
        onClose={() => setSheetOpen(false)} disableSwipeToOpen
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', p: 2 } }}
        {...BOTTOM_SHEET_TRANSITION}
      >
        <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider', mx: 'auto', mb: 2 }} />
        {menuItems}
      </SwipeableDrawer>
    </Paper>
  );

  return <StickyPeekHero expanded={expandedBar} compact={compactBar} />;
}
